import React, { useEffect, useState } from "react";
import { Table, Input, DatePicker, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import NotificationStyle from "./style";
import FetchData from "../../../hooks/FetchData";
import PostUpdateData from "../../../hooks/postUpdate";

// "25th Aug 2024; 08:10 AM" - matches the Figma Notification page exactly.
const formatReceivedOn = (value) => {
  const date = dayjs(value);
  if (!date.isValid()) return "";
  const day = date.date();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  return `${day}${suffix} ${date.format("MMM YYYY; hh:mm A")}`;
};

// Bolds the person's name in a notification's title - "You have received
// a message from Robert William" -> "...from **Robert William**", same
// treatment the Figma design gives every row regardless of type.
const renderSubjectText = (title) => {
  const match = (title || "").match(/^(.*\b(?:from|by)\s)(.+)$/);
  if (!match) return title;
  return (
    <>
      {match[1]}
      <b>{match[2]}</b>
    </>
  );
};

const Notification = () => {
  const navigate = useNavigate();
  const clientId = useSelector((state) => state.authTracking.userInfo?.clientId);

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateFilter, setDateFilter] = useState(null);

  const loadNotifications = () => {
    if (!clientId) return;
    setLoading(true);
    FetchData({ endPoint: `/notification/getnotifications/${clientId}` })
      .then((res) => setNotifications(res?.response || []))
      .catch(() => message.error("Failed to load notifications"))
      .finally(() => setLoading(false));
  };

  useEffect(loadNotifications, [clientId]);

  const handleRowClick = (notification) => {
    if (!notification.is_read) {
      setNotifications((prev) =>
        prev.map((item) =>
          item.notification_id === notification.notification_id ? { ...item, is_read: true } : item
        )
      );
      PostUpdateData({
        endPoint: `/notification/marknotificationread/${clientId}/${notification.notification_id}`,
        method: "PATCH",
      }).catch(() => {});
    }
    if (notification.notification_type === "message" && notification.reference_id) {
      navigate(`/messages/view/${notification.reference_id}`);
    }
  };

  const search = searchText.trim().toLowerCase();
  const filteredNotifications = notifications.filter((item) => {
    if (search && !item.title?.toLowerCase().includes(search)) return false;
    if (dateFilter && !dayjs(item.created_at).isSame(dateFilter, "day")) return false;
    return true;
  });

  const columns = [
    {
      title: "Subject",
      dataIndex: "title",
      key: "title",
      render: (title, record) => (
        <div className="notification-subject-cell">
          {!record.is_read && <span className="notification-unread-dot" />}
          <span className="notification-subject-text">{renderSubjectText(title)}</span>
        </div>
      ),
    },
    {
      title: "Received on",
      dataIndex: "created_at",
      key: "created_at",
      width: 240,
      render: (value) => formatReceivedOn(value),
    },
  ];

  return (
    <NotificationStyle>
      <p className="notification-page-title">Notification</p>

      <div className="notification-filters">
        <Input
          className="notification-search"
          prefix={<SearchOutlined />}
          placeholder="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <DatePicker
          className="notification-date-filter"
          placeholder="Date"
          format="DD/MM/YYYY"
          value={dateFilter}
          onChange={setDateFilter}
          allowClear
        />
      </div>

      <Table
        dataSource={filteredNotifications}
        columns={columns}
        loading={loading}
        rowKey="notification_id"
        rowClassName={(record) => `notification-row${record.is_read ? "" : " unread"}`}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
        })}
      />
    </NotificationStyle>
  );
};

export default Notification;
